import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone, MessageCircle, Sparkles } from "lucide-react";
import { COMPANY } from "../../lib/constants";

const navLinks = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const waLink = `https://wa.me/${COMPANY.whatsapp.replace(/\D/g, "")}?text=${encodeURIComponent(
    "Hi M Brothers, I'd like a free cleaning quote."
  )}`;

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-white/85 border-b border-slate-200/70"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20 md:h-24">
          {/* Logo */}
          <Link
            to="/"
            data-testid="logo-link"
            aria-label="M Brother's Cleaning Services — Home"
            className="flex items-center gap-2 group"
          >
            <img
              src={COMPANY.logoUrl}
              alt="M Brother's Cleaning Services"
              className="h-[72px] md:h-[84px] w-auto object-contain transition-transform duration-300 group-hover:scale-[1.03]"
              loading="eager"
            />
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-9">
            {navLinks.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `text-sm font-medium transition-colors relative ${
                    isActive
                      ? "text-blue-700"
                      : "text-slate-700 hover:text-slate-900"
                  } after:content-[''] after:absolute after:-bottom-1.5 after:left-0 after:h-[2px] after:bg-blue-600 after:transition-all after:duration-300 ${
                    "after:w-0 hover:after:w-full"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          {/* CTAs */}
          <div className="hidden md:flex items-center gap-2">
            <a
              href={`tel:${COMPANY.phoneClean}`}
              data-testid="header-call-btn"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium text-slate-900 hover:bg-slate-100 transition-colors"
            >
              <Phone className="w-4 h-4" />
              <span className="hidden xl:inline">{COMPANY.phone}</span>
              <span className="xl:hidden">Call</span>
            </a>
            <a
              href={waLink}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="header-whatsapp-btn"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium text-white bg-[#25D366] hover:bg-[#1EBE5D] transition-colors active:scale-95"
            >
              <MessageCircle className="w-4 h-4" />
              WhatsApp
            </a>
            <Link
              to="/contact"
              data-testid="header-quote-btn"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 transition-colors active:scale-95 shadow-sm"
            >
              Free Quote
            </Link>
          </div>

          {/* Mobile menu toggle */}
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            className="lg:hidden md:hidden p-2 rounded-md text-slate-900 hover:bg-slate-100"
            aria-label="Toggle menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        {/* Mobile menu */}
        {open && (
          <div
            data-testid="mobile-menu"
            className="md:hidden lg:hidden pb-4 pt-2 border-t border-slate-100 bg-white/95 backdrop-blur"
          >
            <nav className="flex flex-col gap-1">
              {navLinks.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `px-3 py-2.5 rounded-md text-sm font-medium ${
                      isActive
                        ? "bg-blue-50 text-blue-700"
                        : "text-slate-800 hover:bg-slate-50"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <div className="grid grid-cols-2 gap-2 mt-3">
                <a
                  href={`tel:${COMPANY.phoneClean}`}
                  data-testid="mobile-call-btn"
                  className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium border border-slate-200 text-slate-900 hover:bg-slate-50"
                >
                  <Phone className="w-4 h-4" /> Call
                </a>
                <a
                  href={waLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="mobile-whatsapp-btn"
                  className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium text-white bg-[#25D366] hover:bg-[#1EBE5D]"
                >
                  <MessageCircle className="w-4 h-4" /> WhatsApp
                </a>
              </div>
              <Link
                to="/contact"
                data-testid="mobile-quote-btn"
                className="mt-2 flex items-center justify-center gap-2 px-4 py-3 rounded-md text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700"
              >
                Get a Free Quote
              </Link>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
